/**
 * Plantillas predefinidas para los ERPs que ya nos ha pasado el cliente.
 * El usuario puede aplicar una al crear un perfil para no mapear columna a columna.
 *
 * Detección por firma de cabeceras (subset que debe estar presente en orden cualquiera).
 */
import type { IssuedColumnMap, IssuedDefaults } from "@/lib/issuedInvoiceMapping";

export interface IssuedMappingPreset {
  id: string;
  label: string;
  /** Descripción corta para mostrar al usuario en el selector. */
  description: string;
  sheet_index: number;
  header_row: number;
  column_map: IssuedColumnMap;
  defaults: IssuedDefaults;
  /** Cabeceras (normalizadas a minúsculas, sin acentos) que deben aparecer para autodetectar. */
  headerSignature: string[];
  /** Si las facturas se reparten en varias filas (una por tipo de IVA) y hay que agruparlas. */
  groupByInvoice?: boolean;
}

export const ISSUED_PRESETS: IssuedMappingPreset[] = [
  {
    id: "meribia",
    label: "Meribia",
    description: "Plantilla del ERP Meribia (usada por Enora Servicios de Almacenaje y similares).",
    sheet_index: 0,
    header_row: 1,
    column_map: {
      invoiceNumber: "A",
      invoiceDate: "E",
      customerAccount: "F",
      customerName: "J",
      postalCode: "M",
      customerTaxId: "S",
      irpfRate: "Z",
      irpfAmount: "AA",
      taxableBase: "AB",
      vatRate: "AC",
      vatAmount: "AD",
      totalAmount: "AF",
      salesAccount: "AP",
    },
    defaults: {
      dateFormat: "dd/mm/yyyy",
      decimalSeparator: "auto",
      defaultSalesAccount: "70000000",
    },
    headerSignature: ["factura", "fecha", "cliente", "nif", "base", "iva", "total"],
  },
  {
    id: "ferreiro-ventas-clasico",
    label: "Ferreiro – Ventas clásico",
    description:
      "Plantilla clásica de ventas (Ferreiro & Portelo): incluye Recargo de Equivalencia y Forma de pago.",
    sheet_index: 0,
    header_row: 1,
    column_map: {
      invoiceNumber: "A",
      invoiceDate: "B",
      customerAccount: "C",
      customerName: "I",
      customerTaxId: "J",
      postalCode: "M",
      totalAmount: "O",
      irpfRate: "P",
      irpfAmount: "Q",
      vatRate: "R",
      taxableBase: "S",
      vatAmount: "T",
      reRate: "U",
      reAmount: "V",
      paymentMethod: "W",
    },
    defaults: {
      dateFormat: "auto",
      decimalSeparator: "auto",
      defaultSalesAccount: "70000000",
    },
    headerSignature: [
      "numero de factura",
      "porceiva",
      "baseiva",
      "cuotaiva",
      "porcere",
      "cuotare",
      "forma pago",
    ],
  },
];


const normalize = (s: string): string =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[º°ª]/g, "")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Devuelve el primer preset cuya firma esté contenida en las cabeceras del Excel, o null. */
export const detectPresetFromHeaders = (headers: string[]): IssuedMappingPreset | null => {
  const norm = headers.map(normalize);
  for (const p of ISSUED_PRESETS) {
    const ok = p.headerSignature.every((needle) =>
      norm.some((h) => h.includes(normalize(needle))),
    );
    if (ok) return p;
  }
  return null;
};
